import { useMemo } from 'react';
import { safeDivide } from 'src/utils/app';
import { useTrafficMetricsStore } from 'src/zustandStores/TrafficMetrics/useTrafficMetricsStore';

type TrafficType = 'totalClicks' | 'organicClicks' | 'adClicks' | 'otherClicks';

interface UseTrafficMarketShareCalculatorProps {
  trafficType: TrafficType;
}

/**
 * Custom hook that provides a function to calculate market share values
 * for traffic metrics using the total values from the TrafficMetrics store.
 *
 * This hook assumes the TrafficMetricsStore has already been populated with data
 * by a parent component that fetched the traffic summary.
 */
const useTrafficMarketShareCalculator = ({ trafficType }: UseTrafficMarketShareCalculatorProps) => {
  // Get the traffic value selectors from the store
  const getTrafficValue = useTrafficMetricsStore((state) => state.getTrafficValue);
  const getComparisonTrafficValue = useTrafficMetricsStore((state) => state.getComparisonTrafficValue);

  // Get traffic loading state
  const trafficSummary = useTrafficMetricsStore((state) => state.trafficSummary);
  const comparisonTrafficSummary = useTrafficMetricsStore((state) => state.comparisonTrafficSummary);
  const isLoading = useTrafficMetricsStore((state) => state.isLoading);

  // Check if primary and comparison data are loaded
  const isPrimaryDataLoaded = !!trafficSummary;
  const isComparisonDataLoaded = !!comparisonTrafficSummary;

  // Overall data loaded state - both primary and comparison data are loaded and not loading
  const isDataLoaded = isPrimaryDataLoaded && !isLoading;

  // Create a memoized market share calculator function that handles both primary and comparison data
  const calculateMarketShare = useMemo(() => {
    return (value: number, isComparison: boolean = false): number => {
      // Get the appropriate denominator and check if data is loaded
      const dataLoaded = isComparison ? isComparisonDataLoaded : isPrimaryDataLoaded;
      const denominator = isComparison ? getComparisonTrafficValue(trafficType) : getTrafficValue(trafficType);

      // Calculate market share - return 0 if data isn't loaded yet or denominator is 0
      if (!dataLoaded || !denominator || denominator === 0) {
        return 0;
      }

      return safeDivide(value, denominator);
    };
  }, [trafficType, getTrafficValue, getComparisonTrafficValue, isPrimaryDataLoaded, isComparisonDataLoaded]);

  return {
    calculateMarketShare,
    isDataLoaded,
    isPrimaryDataLoaded,
    isComparisonDataLoaded
  };
};

export default useTrafficMarketShareCalculator;
